import ApartmentIcon from "@mui/icons-material/Apartment";
import { HealingOutlined } from "@mui/icons-material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonIcon from "@mui/icons-material/Person";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_LINK } from "../../common/menu/menuItems/menuList";
import {
  addAdmission,
  resetCurrentAdmission,
} from "../../redux/admissions/admissionsActions";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchDepartments } from "../../redux/department/departmentAction";
import { fetchFloors } from "../../redux/floor/floorAction";
import {
  fetchAdmissionPatients,
} from "../../redux/patient/patientAction";
import { fetchWards } from "../../redux/ward/wardAction";
import { formatToOptions } from "./admissionUtils";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getCurrentFormattedDate = () => new Date().toISOString().split("T")[0];

const clearLocationStorage = () => {
  localStorage.removeItem("block");
  localStorage.removeItem("floor");
  localStorage.removeItem("ward");
};

/** Format date as "YYYY-MM-DD HH:mm" using actual current time */
const formatSubmitDate = (dateString) => {
  const selected = new Date(dateString);
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${selected.getFullYear()}-${pad(selected.getMonth() + 1)}-${pad(
    selected.getDate()
  )} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

// ---------------------------------------------------------------------------
// Custom hook
// ---------------------------------------------------------------------------
const useAddAdmissionsForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "",
    message: "",
    type: "success",
  });

  const storedBlock = localStorage.getItem("block") || "";
  const storedFloor = localStorage.getItem("floor") || "";

  const [patientPage, setPatientPage] = useState(1);
  const [patientSearch, setPatientSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ---- form setup --------------------------------------------------------
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      patient_id: "",
      block_id: "",
      floor_id: "",
      ward_id: "",
      department_id: "",
      attender_type: "1",
      no_of_attenders: "1",
      attender_name: "",
      attender_relation: "",
      attender_address: "",
      attender_phone: "",
      attender_name_2: "",
      attender_relation_2: "",
      attender_address_2: "",
      attender_phone_2: "",
      admit_date: getCurrentFormattedDate(),
    },
  });

  // ---- selectors ---------------------------------------------------------
  const blockList        = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList        = useSelector((state) => state?.floor?.fetchFloors || {});
  const departmentList   = useSelector((state) => state?.department?.fetchDepartments || {});
  const wardList         = useSelector((state) => state?.ward?.fetchWards || {});
  const patientList      = useSelector((state) => state?.patients?.admissionPatients || {});
  const admissionState   = useSelector((state) => state?.admission?.addAdmission || {});

  console.log("blockList", blockList);
  console.log("floorList", floorList);
  console.log("departmentList", departmentList);
  console.log("wardList", wardList);
  console.log("patientList", patientList);
  console.log("admissionState", admissionState);
  const isBlockLoading      = useSelector((state) => state?.block?.fetchBlocks?.inProgress || false);
  const isFloorLoading      = useSelector((state) => state?.floor?.fetchFloors?.inProgress || false);
  const isWardLoading       = useSelector((state) => state?.ward?.fetchWards?.inProgress || false);
  const isDepartmentLoading = useSelector((state) => state?.department?.fetchDepartments?.inProgress || false);
  const isPatientLoading    = useSelector((state) => state?.patients?.admissionPatients?.inProgress || false);

  // ---- watched values ----------------------------------------------------
  const watchPatientId     = watch("patient_id");
  const watchAttenderType  = watch("attender_type");
  const watchNoOfAttenders = watch("no_of_attenders");
  const watchBlock         = watch("block_id");
  const watchFloor         = watch("floor_id");

  // ---- initial data fetch ------------------------------------------------
  useEffect(() => {
    dispatch(fetchAdmissionPatients({ pageNo: patientPage, pageSize: 10, search: debouncedSearch }));
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
  }, [dispatch, patientPage, debouncedSearch]);

  // ---- debounce search ----------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(patientSearch);
      setPatientPage(1); // Reset to page 1 on new search
    }, 2000); // 2 second debounce

    return () => clearTimeout(timer);
  }, [patientSearch]);

  // ---- set today's date --------------------------------------------------
  useEffect(() => {
    setValue("admit_date", getCurrentFormattedDate());
  }, [setValue]);


  // ---- pre-fill patient address / phone when patient selection changes --------
  useEffect(() => {
    if (watchPatientId && patientList?.data?.data) {
      const selectedPatient = patientList.data.data.find(p => p.id === watchPatientId);
      if (selectedPatient) {
        setValue("attender_address", selectedPatient.address || "");
        setValue("attender_phone", selectedPatient.phone || "");
      }
    }
  }, [watchPatientId, patientList, setValue]);

  // ---- block → fetch floors, reset dependents ----------------------------
  useEffect(() => {
    if (watchBlock) {
      dispatch(fetchFloors(watchBlock));
      if (watchBlock !== storedBlock) {
        setValue("floor_id", "");
        setValue("ward_id", "");
      }
    }
  }, [watchBlock, dispatch, setValue, storedBlock]);

  // ---- block + floor → fetch wards, reset ward ---------------------------
  useEffect(() => {
    if (watchBlock && watchFloor) {
      dispatch(fetchWards(watchBlock, watchFloor));
      if (watchFloor !== storedFloor) setValue("ward_id", "");
    }
  }, [watchBlock, watchFloor, dispatch, setValue, storedFloor]);

  // ---- persist location selections to localStorage -----------------------
  useEffect(() => {
    if (watchBlock) localStorage.setItem("block", watchBlock);
    if (watchFloor) localStorage.setItem("floor", watchFloor);
  }, [watchBlock, watchFloor]);

  // ---- handle admission success / error ----------------------------------
  useEffect(() => {
    if (admissionState.success) {
      if (watchAttenderType === "1") {
        navigate(`/admission/view/${watchPatientId}`);
      } else {
        navigate(APP_LINK.LISTADMISSIONDETAILS);
      }
      dispatch(resetCurrentAdmission());
    } else if (admissionState.error) {
      setDialogProps({
        title: "Error",
        message: "Failed to submit Admission request. Please try again.",
        type: "error",
      });
      setDialogOpen(true);
      dispatch(resetCurrentAdmission());
    }
  }, [admissionState, dispatch, navigate, watchAttenderType, watchPatientId]);

  // ---- handlers ----------------------------------------------------------
  const handleCancel = () => {
    reset();
    clearLocationStorage();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (dialogProps.type === "success") {
      reset();
      clearLocationStorage();
    }
    navigate(APP_LINK.LISTADMISSIONDETAILS);
  };

  const onSubmit = (data) => {
    if (!data.block_id || !data.floor_id || !data.ward_id) {
      setDialogProps({
        title: "Error",
        message: "Please select Block, Floor and Ward",
        type: "error",
      });
      setDialogOpen(true);
      return;
    }

    const formdata = {
      patient_id:    data.patient_id,
      block_id:      data.block_id,
      floor_id:      data.floor_id,
      ward_id:       data.ward_id,
      department_id: data.department_id || "",
      admit_date:    formatSubmitDate(data.admit_date),
      attender_type: data.attender_type,
    };

    if (data.attender_type === "1") {
      const attenderDetails = [
        {
          attender_name: data.attender_name,
          relation:      data.attender_relation,
          address:       data.attender_address,
          phone:         data.attender_phone,
        },
      ];
      if (data.no_of_attenders === "2") {
        attenderDetails.push({
          attender_name: data.attender_name_2,
          relation:      data.attender_relation_2,
          address:       data.attender_address_2,
          phone:         data.attender_phone_2,
        });
      }
      formdata.number_attenders = data.no_of_attenders;
      formdata.attender_details = attenderDetails;
    } else {
      formdata.number_attenders = "0";
      formdata.attender_details = [];
    }

    dispatch(addAdmission(formdata));
  };

  const handlePatientInputChange = (event, newInputValue, reason) => {
    if (reason === "input") {
      setPatientSearch(newInputValue);
    }
  };

  const handlePatientPageChange = (direction) => {
    if (direction === "next") {
      setPatientPage((prev) => prev + 1);
    } else if (direction === "prev") {
      setPatientPage((prev) => Math.max(1, prev - 1));
    }
  };

  // ---- memoised option builders -----------------------------------------
  const patientOptions = useMemo(() => {
    const uniquePatients = [];
    const seenLabels = new Set();
    const dataList = patientList?.data?.data || [];

    dataList.forEach((patient) => {
      const label = `${patient.patient_name} - ${patient.phone}- ${patient.ip_number}`;
      if (patient.admission_status !== 1 && !seenLabels.has(label)) {
        seenLabels.add(label);
        uniquePatients.push({ value: patient.id, label });
      }
    });
    return uniquePatients;
  }, [patientList?.data?.data]);

  const blockOptions = useMemo(
    () => formatToOptions(blockList?.Blocks?.data, "block_name", "id", (b) => b.block_status !== 2),
    [blockList?.Blocks?.data]
  );

  const floorOptions = useMemo(
    () => formatToOptions(floorList?.floors?.data, "floor_name", "id", (f) => f.floor_status !== 0),
    [floorList?.floors?.data]
  );

  const wardOptions = useMemo(
    () => formatToOptions(wardList?.wards?.data, "ward_name", "id", (w) => w.status !== 0),
    [wardList?.wards?.data]
  );

  const departmentOptions = useMemo(
    () => formatToOptions(departmentList?.departments?.data, "department_name", "id", (d) => d.status !== 2),
    [departmentList?.departments?.data]
  );

  // ---- memoised field arrays with dynamic options -----------------------
  const patientFields = useMemo(
    () => [
      {
        name: "patient_id",
        label: "Select Patient / நோயாளியை தேர்ந்தெடுக்கவும்",
        type: "select",
        tabIndex: 1,
        options: patientOptions,
        rules: { required: "Patient selection is required" },
        startIcon: <PersonIcon />,
        loading: isPatientLoading,
        loadingText: "Loading patients... / நோயாளிகள் ஏற்றப்படுகிறார்கள்...",
        noOptionsText: patientSearch && !isPatientLoading ? "Matching patients not found / தேடும் நோயாளி இல்லை" : "No Patients found / நோயாளிகள் இல்லை",
        onInputChange: handlePatientInputChange,
        onPageChange: handlePatientPageChange,
        hasNextPage: (patientList?.data?.data || []).length === 10, // Assuming 10 is the page size
        hasPrevPage: patientPage > 1,
      },
      {
        name: "admit_date",
        label: "Admission Date / சேர்க்கை தேதி",
        type: "date",
        tabIndex: 2,
        rules: { required: "Admission Date is required" },
        startIcon: <PersonIcon />,
        defaultValue: getCurrentFormattedDate(),
      },
    ],
    [patientOptions, isPatientLoading]
  );

  const locationFields = useMemo(
    () => [
      {
        name: "block_id",
        label: "Block / கட்டிடம்",
        placeholder: "Select block",
        type: "select",
        tabIndex: 3,
        options: blockOptions,
        rules: { required: "Block is required" },
        startIcon: <LocalHospitalIcon />,
        loading: isBlockLoading,
        loadingText: "Loading blocks... / கட்டிடங்கள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No blocks found / கட்டிடங்கள் இல்லை",
      },
      {
        name: "ward_id",
        label: "Ward Name / வார்டு பெயர்",
        placeholder: "Select ward name",
        type: "select",
        tabIndex: 5,
        options: wardOptions,
        rules: { required: "Ward name is required" },
        startIcon: <MeetingRoomIcon />,
        loading: isWardLoading,
        loadingText: "Loading wards... / வார்டுகள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No wards found / வார்டுகள் இல்லை",
      },
    ],
    [blockOptions, wardOptions, isBlockLoading, isWardLoading]
  );

  const wardDeptFields = useMemo(
    () => [
      {
        name: "floor_id",
        label: "Floor / மாடி",
        placeholder: "Select Floor",
        type: "select",
        tabIndex: 4,
        options: floorOptions,
        rules: { required: "Floor is required" },
        startIcon: <ApartmentIcon />,
        loading: isFloorLoading,
        loadingText: "Loading floors... / மாடிகள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No floors found / மாடிகள் இல்லை",
      },
      {
        name: "department_id",
        label: "Department /துறை",
        placeholder: "Select department",
        type: "select",
        tabIndex: 6,
        options: departmentOptions,
        rules: { required: "Department is required" },
        startIcon: <HealingOutlined />,
        loading: isDepartmentLoading,
        loadingText: "Loading departments... / துறைகள் ஏற்றப்படுகின்றன...",
        noOptionsText: "No departments found / துறைகள் இல்லை",
      },
    ],
    [floorOptions, departmentOptions, isFloorLoading, isDepartmentLoading]
  );

  return {
    // form controls
    register,
    handleSubmit,
    setValue,
    control,
    errors,
    // watched values
    watchAttenderType,
    watchNoOfAttenders,
    // state
    admissionState,
    onSubmit,
    handleCancel,
    // dialog
    dialogOpen,
    dialogProps,
    handleCloseDialog,
    // field arrays (dynamic)
    patientFields,
    locationFields,
    wardDeptFields,
  };
};

export default useAddAdmissionsForm;
